/**
 * JurisdictionRequestsPage
 * ========================
 * Session 24 (Feb 22, 2026)
 *
 * Approval queue for jurisdiction-creation proposals.
 *
 *   • Admin + Jurisdiction Manager → see ALL requests, can Approve / Reject.
 *   • Jurisdiction Requestor → sees ONLY their own submissions; rejected ones
 *     carry an inline "Revise & Resubmit" button that reopens the wizard
 *     prefilled with the last payload.
 *
 * Deliberately kept as a single page (not split admin vs. self) because both
 * audiences need the same tabular information. Role gating happens per-row
 * via the action buttons.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MlsEntityReviewDialog, originText } from '../components/mls/MlsEntityReviewDialog';
import { SERVICE_LABELS } from '../components/mls/mlsServiceTypes';
import { toast } from 'sonner';
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCcw,
  AlertCircle,
  Eye,
  X,
  MapPin,
  Phone,
  Globe,
  FileText,
  Loader2,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Search, Building2, Link2, Layers, Flag, LayoutList, Landmark, ChevronDown, ShieldCheck, Pencil, FileAudio, Download, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { CorrectionReviewModal } from '../components/jurisdiction/CorrectionReviewModal';
import { CORRECTION_TYPE_LABEL, STATE_NAMES } from '../components/jurisdiction/RequestJurisdictionCorrectionDialog';
import { displayTitleCase, displayUSAddress } from '../utils/formatters';
import { formatUSPhoneDisplay } from '../utils/phoneFormat';

// PJR-001 — request-source labels keyed by `origin`; each originating workflow gets its own label.
const ORIGIN_LABELS = { search_fulfillment: 'Tax Search Fulfillment' };
const originLabel = (origin) => ORIGIN_LABELS[origin] || null;
// PJR-001 P1 — states whose jurisdictions use the Tax Collector master-data / assignment model.
import { Pagination } from '../components/crm/Pagination';
import './jurisdiction-requests.css';
import { InlineAudioPlayer, isAudioAttachment } from '../components/jurisdiction/InlineAudioPlayer';
import { MlcCollapse } from '../components/jurisdiction/MlcCollapse';
import { CorrectionReviewActions, ISSUE_TYPE_LABELS } from '../components/jurisdiction/CorrectionReviewActions';
import { listCorrectionRequests, listMyCorrectionRequests } from '../utils/taxCollectorsApi';
import { getUserFromToken } from '../utils/adminApi';
import { Button } from '../components/ui/button';
import JurisdictionWizardModal from '../components/jurisdiction/JurisdictionWizardModal';
import { validateCollectorField } from '../components/jurisdiction/RequestMissingJurisdictionDialog';
import { getJurisdictionTypeLabel } from '../lib/jurisdictionTaxonomy';
import { formatInTimeZone } from 'date-fns-tz';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const ET_TIMEZONE = 'America/New_York';

const STATUS_META = {
  pending: { label: 'Pending', icon: Clock, className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  approved: { label: 'Approved', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', icon: XCircle, className: 'bg-rose-50 text-rose-700 border-rose-200' },
  duplicate: { label: 'Duplicate', icon: AlertCircle, className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

// JURISDICTION-REQUESTS-CONSOLIDATION-001 — category control (URL ?category=)
const CATEGORIES = [
  { key: 'all', label: 'All Requests', icon: LayoutList },
  { key: 'new_jurisdiction', label: 'New Jurisdiction', icon: Building2 },
  { key: 'research_links', label: 'Research Links', icon: Link2 },
  { key: 'mls_service_entity', label: 'MLS Service Entity', icon: Layers },
  { key: 'corrections', label: 'Collector Corrections', icon: Flag },
  { key: 'directory_corrections', label: 'Directory Corrections', icon: Pencil },  // JCR-001
];
const requestCategory = (r) => (r.request_type === 'research_links' || r.request_type === 'mls_service_entity') ? r.request_type : r.request_type === 'jurisdiction_correction' ? 'directory_corrections' : 'new_jurisdiction';
const DATE_PRESETS = [['all', 'All time'], ['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days']];

const formatET = (iso) => {
  if (!iso) return '—';
  try {
    return formatInTimeZone(new Date(iso), ET_TIMEZONE, "MMM d, yyyy h:mm a 'ET'");
  } catch {
    return iso;
  }
};

// Table cell: date on line 1, time on line 2 — deterministic 2-line layout.
const SubmittedCell = ({ iso }) => {
  if (!iso) return <span className="text-xs text-slate-400">—</span>;
  try {
    const d = new Date(iso);
    return (
      <div className="text-xs text-slate-500 leading-snug whitespace-nowrap">
        <div>{formatInTimeZone(d, ET_TIMEZONE, 'MMM d, yyyy')}</div>
        <div className="text-slate-400">{formatInTimeZone(d, ET_TIMEZONE, "h:mm a 'ET'")}</div>
      </div>
    );
  } catch {
    return <span className="text-xs text-slate-500">{iso}</span>;
  }
};

const TD = 'px-2 py-2.5 align-middle';

// Names wrap on spaces; unbreakable tokens (e-mails) truncate with a tooltip instead of splitting mid-word.
const ByCell = ({ name }) => {
  const v = name || '—';
  return /\s/.test(v) ? <span className="break-words">{v}</span> : <div className="truncate" title={v}>{v}</div>;
};

// Small inline reject modal — kept local because it only lives in this flow.
const RejectPrompt = ({ open, onCancel, onConfirm, initialReason = '' }) => {
  const [reason, setReason] = useState('');
  // Sync seed value whenever the modal opens with a (possibly different)
  // pre-filled reason — e.g. "Reject as Duplicate" pre-fills with the
  // matched jurisdiction's id/name.
  useEffect(() => {
    if (open) setReason(initialReason || '');
  }, [open, initialReason]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1110] flex items-center justify-center bg-black/50">
      <div className="w-[420px] bg-white rounded-xl shadow-2xl p-5" data-testid="reject-prompt">
        <h3 className="text-base font-semibold text-slate-800 mb-2">Reject Request</h3>
        <p className="text-xs text-slate-500 mb-3">
          Provide a clear reason — the requestor will see it and can revise & resubmit.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          placeholder="e.g., Duplicate of existing jurisdiction; please verify source document"
          data-testid="reject-reason-input"
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onCancel} data-testid="reject-cancel">Cancel</Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={reason.trim().length < 3}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="reject-confirm"
          >
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Module-scope Field / Section components (May 1, 2026 — focus-loss fix)
// ─────────────────────────────────────────────────────────────────────
// Previously these were declared INSIDE RequestDetailsModal's body. That
// meant every keystroke (which updates editedFields state) triggered a
// parent re-render, which re-created the Field/Section function
// identities, which forced React to unmount/remount every <Field> —
// including the active <input>, causing focus loss after each character.
// Hoisting them to module scope keeps their component identity stable
// across re-renders; the edit state flows in via React Context without
// touching the ~30 existing <Field> call sites.
const EditCtx = createContext({
  isEditing: false,
  effectivePayload: {},
  originalPayload: {},
  editedFields: {},
  serverEditedPayload: {},
  updateEditedField: () => {},
  updateEditedFields: () => {},
});

const Section = ({ icon: Icon, title, children, testId, badge = null }) => {
  const [openSec, setOpenSec] = useState(true);
  return (
    <div className="mb-3 rounded-2xl border border-blue-100 bg-white shadow-[0_1px_3px_rgba(30,64,175,0.06)] overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpenSec((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/80 transition-colors" data-testid={`${testId}-toggle`}>
        <Icon className="w-4 h-4 text-indigo-600" />
        <h4 className="text-[15px] font-semibold text-slate-900 flex-1">{title}</h4>
        {badge}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-150 ${openSec ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-150 ease-out ${openSec ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-4 pb-4 pt-1 border-t border-slate-100">{children}</div>
        </div>
      </div>
    </div>
  );
};

// PJR-002: fetch with bearer token and open in a new tab (secure download)
// PJR-REVIEW-FIX-001: one secure fetch for view/download; errors mapped to clear messages.
const attachmentError = (status) => {
  if (status === 404) return 'This attachment is no longer available in secure storage';
  if (status === 403) return 'You are not authorized to access this attachment';
  if (status === 401) return 'Your session has expired — sign in again to open attachments';
  if (status === 503) return 'Secure file storage is temporarily unavailable';
  return status ? `Attachment could not be loaded (HTTP ${status})` : 'Attachment could not be loaded (network error)';
};
const fetchAttachmentBlob = async (requestId, a) => {
  let r;
  try {
    r = await fetch(`${API_BASE_URL}/api/jurisdiction-requests/${requestId}/attachments/${a.file_id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  } catch { throw new Error(attachmentError(0)); }
  if (!r.ok) throw new Error(attachmentError(r.status));
  const blob = await r.blob();
  return a.content_type && blob.type !== a.content_type ? blob.slice(0, blob.size, a.content_type) : blob;
};

// PJR-AUDIO-UPLOAD-001: secure download of a recording (bearer-token fetch → object URL → save)
const downloadAttachment = async (requestId, a) => {
  try {
    const url = URL.createObjectURL(await fetchAttachmentBlob(requestId, a));
    const el = document.createElement('a'); el.href = url; el.download = a.filename || 'attachment'; el.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) { toast.error(`Could not download ${a.filename}: ${e.message}`); }
};

/** PJR-REVIEW-FIX-001: in-app secure preview (PDF via iframe, images via <img>) — no pop-ups, bearer-fetched blob only. */
const AttachmentPreviewDialog = ({ requestId, attachment, onClose }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!attachment) return undefined;
    let objUrl = null; let alive = true;
    setUrl(null); setError('');
    fetchAttachmentBlob(requestId, attachment)
      .then((blob) => { if (!alive) return; objUrl = URL.createObjectURL(blob); setUrl(objUrl); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [requestId, attachment]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!attachment) return null;
  const isPdf = attachment.content_type === 'application/pdf';
  return (
    /* plain overlay above the details modal (z-[1100]) — shadcn Dialog (z-50) would render behind it */
    <div className="fixed inset-0 z-[1120] flex items-center justify-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label={`Preview ${attachment.filename}`} data-testid="attachment-preview-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <FileText className={`h-4 w-4 shrink-0 ${isPdf ? 'text-rose-500' : 'text-indigo-500'}`} />
          <h3 className="text-[14px] font-semibold text-slate-800 truncate flex-1" data-testid="attachment-preview-title">{attachment.filename}</h3>
          <span className="text-[12px] text-slate-400 shrink-0">{(attachment.size / 1024).toFixed(0)} KB</span>
          <Button size="sm" variant="outline" className="h-8 text-[12.5px]" onClick={() => downloadAttachment(requestId, attachment)} data-testid="attachment-preview-download"><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
          <button type="button" onClick={onClose} aria-label="Close preview" className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100" data-testid="attachment-preview-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 min-h-0 bg-slate-100">
          {error ? (
            <div className="h-full grid place-items-center p-6 text-center" data-testid="attachment-preview-error">
              <div><AlertTriangle className="h-6 w-6 text-amber-500 mx-auto mb-2" /><p className="text-[13px] font-medium text-slate-700">{error}</p><p className="text-[12px] text-slate-500 mt-1">Ask the submitter to re-upload the document if it is still required.</p></div>
            </div>
          ) : !url ? (
            <div className="h-full grid place-items-center text-[13px] text-slate-500" data-testid="attachment-preview-loading"><span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading document…</span></div>
          ) : isPdf ? (
            <div className="h-full flex flex-col">
              <iframe title={attachment.filename} src={url} className="w-full flex-1 border-0" data-testid="attachment-preview-frame" />
              <div className="px-4 py-1.5 border-t border-slate-200 bg-white text-[11.5px] text-slate-500 flex items-center justify-between">
                <span>Secure preview · bearer-authenticated, not a public link</span>
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-700 underline" data-testid="attachment-preview-open-tab">Document not displaying? Open in a new tab</a>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-auto grid place-items-center p-4"><img src={url} alt={attachment.filename} className="max-w-full max-h-full rounded-lg shadow" data-testid="attachment-preview-image" /></div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({
  label,
  value,
  mono = false,
  className = '',
  editKey = null,
  editType = 'text',      // 'text' | 'textarea' | 'select'
  editOptions = [],        // for select only
  editPlaceholder = '',
  emptyText = null,        // view mode: render this muted text instead of hiding the field when empty
  disabled = false,        // PJR-002-B: inactive (MLC-dependent field while MLC Required = No)
  disabledText = 'Not applicable',
  onEditChange = null,     // optional override of the default single-key updater
}) => {
  const {
    isEditing,
    effectivePayload,
    originalPayload,
    editedFields,
    serverEditedPayload,
    updateEditedField,
  } = useContext(EditCtx);

  const editable = isEditing && editKey != null;
  const editedVal = editable ? (effectivePayload[editKey] ?? '') : null;
  const originalVal = editable ? originalPayload[editKey] : null;
  const isModified =
    editable &&
    (editedFields[editKey] !== undefined ||
      serverEditedPayload[editKey] !== undefined) &&
    String(editedVal ?? '') !== String(originalVal ?? '');
  const change = onEditChange || updateEditedField;

  if (!editable) {
    const display =
      value === null || value === undefined || value === ''
        ? null
        : typeof value === 'boolean'
          ? value ? 'Yes' : 'No'
          : String(value);
    if (display === null && !emptyText && !disabled) return null;
    const tid = `detail-${label.toLowerCase().replace(/\s+/g, '-')}`;
    return (
      <div className={`${className} ${disabled ? 'opacity-60' : ''}`} data-testid={tid} aria-disabled={disabled || undefined}>
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">
          {label}
        </p>
        {disabled && display === null
          ? <p className="text-sm italic text-slate-400" data-testid={`${tid}-disabled`}>{disabledText}</p>
          : display === null
          ? <p className="text-sm italic text-slate-400" data-testid={`${tid}-empty`}>{emptyText}</p>
          : <p className={`text-sm text-slate-800 ${mono ? 'font-mono' : ''}`}>{display}</p>}
      </div>
    );
  }

  // ---- Edit mode ----
  const inputCls = `w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed`;
  return (
    <div className={`${className} ${disabled ? 'opacity-60' : ''}`} data-testid={`edit-${editKey}`} aria-disabled={disabled || undefined}>
      <div className="flex items-center gap-2 mb-0.5">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
          {label}
        </p>
        {isModified && (
          <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5 uppercase tracking-wider">
            Modified
          </span>
        )}
      </div>
      {editType === 'textarea' ? (
        <textarea
          value={editedVal}
          onChange={(e) => change(editKey, e.target.value)}
          placeholder={disabled ? disabledText : editPlaceholder}
          disabled={disabled}
          className={`${inputCls} resize-y min-h-[60px]`}
          data-testid={`edit-input-${editKey}`}
        />
      ) : editType === 'select' ? (
        <select
          value={typeof editedVal === 'boolean' ? String(editedVal) : (editedVal ?? '')}
          onChange={(e) => change(editKey, e.target.value === 'true' ? true : e.target.value === 'false' ? false : e.target.value === '' && editOptions.some((o) => o.value === 'true') ? null : e.target.value)}
          disabled={disabled}
          className={inputCls}
          data-testid={`edit-input-${editKey}`}
        >
          {/* MLC-REVIEW-YESNO-BUG-001: binary selects have no blank option; a legacy null value shows a non-selectable placeholder */}
          {(editedVal === null || editedVal === undefined || editedVal === '') && !editOptions.some((o) => o.value === '') && (
            <option value="" disabled>Not set</option>
          )}
          {editOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{disabled && opt.value === '' ? disabledText : opt.label}</option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={editedVal}
          onChange={(e) => change(editKey, e.target.value)}
          placeholder={disabled ? disabledText : editPlaceholder}
          disabled={disabled}
          className={`${inputCls} ${mono ? 'font-mono' : ''}`}
          data-testid={`edit-input-${editKey}`}
        />
      )}
      {isModified && !disabled && (
        <p className="text-[10px] text-slate-500 mt-0.5 italic truncate">
          was: {typeof originalVal === 'boolean' ? (originalVal ? 'Yes' : 'No') : (originalVal || '—')}
        </p>
      )}
    </div>
  );
};

/**
 * ParentCountyField — special-case row for the Basic Info section.
 *
 * Why a custom component instead of using <Field>?
 *   • View mode needs a smart fallback: payload.parent_county_name → a
 *     hydrated lookup (when older requests lack the name) → finally the
 *     UUID as a last resort. The generic <Field> can't express the
 *     hydrated lookup without leaking module-internal state.
 *   • Edit mode must be a state-scoped Select of real counties (id +
 *     name), not a free-form UUID textbox. Picking a county MUST update
 *     parent_county_id AND parent_county_name together — so we use
 *     EditCtx's batch updater.
 */
const ParentCountyField = ({
  payload,
  isEditing,
  hydratedName,
  options,
  originalId,
  editedFields,
  serverEditedPayload,
  updateEditedFields,
}) => {
  const currentId = payload.parent_county_id || '';
  const currentName =
    payload.parent_county_name ||
    hydratedName ||
    (options.find((c) => c.id === currentId)?.jurisdiction_name) ||
    '';
  // Jun 2026 — LA terminology: Louisiana's county-equivalent level is the
  // Parish. Mirrors backend/models/jurisdiction.py::get_parent_terminology().
  const parentNoun = (payload.state || '').toUpperCase() === 'LA' ? 'Parish' : 'County';
  const parentNounPlural = parentNoun === 'Parish' ? 'parishes' : 'counties';

  if (!isEditing) {
    // Display the resolved name; only fall back to UUID if absolutely
    // nothing else is available (e.g. the parent county was deleted from
    // the directory). That last-resort UUID path is intentional —
    // showing nothing would be worse, since it hides a real data issue.
    const display = currentName ? displayTitleCase(currentName) : currentId;
    if (!display) return null;
    return (
      <div data-testid="detail-parent-county">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">
          Parent {parentNoun}
        </p>
        <p className="text-sm text-slate-800">{display}</p>
      </div>
    );
  }

  const isModified =
    (editedFields.parent_county_id !== undefined ||
      serverEditedPayload.parent_county_id !== undefined) &&
    String(currentId) !== String(originalId || '');

  return (
    <div data-testid="edit-parent_county_id">
      <div className="flex items-center gap-2 mb-0.5">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
          Parent {parentNoun}
        </p>
        {isModified && (
          <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5 uppercase tracking-wider">
            Modified
          </span>
        )}
      </div>
      <select
        value={currentId}
        onChange={(e) => {
          const newId = e.target.value;
          const match = options.find((c) => c.id === newId);
          updateEditedFields({
            parent_county_id: newId,
            parent_county_name: match?.jurisdiction_name || '',
          });
        }}
        className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
        data-testid="edit-input-parent_county_id"
      >
        <option value="">— Select parent {parentNoun.toLowerCase()} —</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>
            {c.jurisdiction_name}
          </option>
        ))}
      </select>
      {options.length === 0 && (
        <p className="text-[10px] text-slate-500 mt-0.5 italic">
          No {parentNounPlural} found for {payload.state || 'this state'}. Add a {parentNoun} jurisdiction first.
        </p>
      )}
      {isModified && (
        <p className="text-[10px] text-slate-500 mt-0.5 italic truncate">
          was: {(options.find((c) => c.id === originalId)?.jurisdiction_name) || originalId || '—'}
        </p>
      )}
    </div>
  );
};

/**
 * Read-only details modal — shows every field a Jurisdiction Requestor
 * submitted, so the Manager has full context before approving/rejecting.
 *
 * Feb 25, 2026 (V2): adds duplicate-detection awareness.
 *   • On open, queries `/api/jurisdictions/search/check-duplicates` for
 *     name+state+type and surfaces an info banner when matches exist.
 *   • Includes any `near_duplicate_ids` the requestor was warned about
 *     (persisted on the request doc), plus the requestor's `justification`.
 *   • New "Compare with existing" toggle → 2-column field-level diff
 *     (existing vs proposed) with amber highlighting on differences.
 *   • New "Reject as Duplicate" shortcut → pre-fills the reject reason.
 */
const RequestDetailsModal = ({
  open,
  request,
  isReviewer,
  allRequests = [],
  onClose,
  onApproved,
  onReject,
  onRejectAsDuplicate,
  onEditApproved,
}) => {
  const [dupCheck, setDupCheck] = useState({ loading: false, exact: [], near: [], otherCounty: [], parentScoped: true });
  const [showCompare, setShowCompare] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [activeMatch, setActiveMatch] = useState(null);

  // Feb 27, 2026 — Reviewer edit mode. `editedFields` holds only the
  // fields the reviewer changed in THIS session. `effectivePayload` is
  // what the duplicate check + form bind against: original ← payload_edited
  // (from server, if any) ← editedFields (local, unsaved).
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [saving, setSaving] = useState(false);
  // PJR-001 (Oct 2026) — in-popup approval lifecycle: in-flight guard, backend-confirmed result, inline error.
  const [approving, setApproving] = useState(false);
  const [approveResult, setApproveResult] = useState(null);
  const [approveError, setApproveError] = useState('');

  // Feb 22, 2026 — Parent County hydration & dropdown options.
  // Two problems we solve here:
  //   1. Older proposals were submitted without `parent_county_name`, so
  //      the view modal used to render the raw `parent_county_id` UUID.
  //      We backfill the name from the live jurisdictions collection on
  //      open so the display always shows a human-readable label.
  //   2. The Edit field for parent county used to be a free-form text
  //      input asking the reviewer to paste a UUID. Now we load the list
  //      of counties for the proposal's state and present them as a
  //      Select. Picking a county updates BOTH parent_county_id and
  //      parent_county_name in the edited payload.
  const [hydratedParentName, setHydratedParentName] = useState(null);
  const [parentCountyOptions, setParentCountyOptions] = useState([]);
  // PJR-001-B / D1 — optional collector linkage chosen by the reviewer (never pre-filled, never implicit).
  const [previewAttachment, setPreviewAttachment] = useState(null); // PJR-REVIEW-FIX-001

  const originalPayload = useMemo(() => request?.payload || {}, [request]);
  const serverEditedPayload = useMemo(
    () => request?.payload_edited || {},
    [request]
  );
  const effectivePayload = useMemo(
    () => ({ ...originalPayload, ...serverEditedPayload, ...editedFields }),
    [originalPayload, serverEditedPayload, editedFields]
  );
  // For read-only view, `p` reflects the authoritative latest value;
  // for edit mode, we bind inputs to `effectivePayload` directly.
  const p = effectivePayload;

  // Hit the duplicate endpoint whenever the modal opens with a fresh request.
  useEffect(() => {
    if (!open || !request) return;
    const name = p.jurisdiction_name;
    const state = p.state;
    if (!name || !state) {
      setDupCheck({ loading: false, exact: [], near: [] });
      return;
    }
    let cancelled = false;
    setDupCheck((prev) => ({ ...prev, loading: true }));
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ name, state });
        if (p.jurisdiction_type) params.set('jurisdiction_type', p.jurisdiction_type);
        // PJR-001 county-aware duplicates: parent id when known, normalized parent name as fallback
        if (p.parent_county_id) params.set('parent_county_id', p.parent_county_id);
        else if (p.parent_county_name) params.set('parent_county_name', p.parent_county_name);
        const res = await axios.get(
          `${API_BASE_URL}/api/jurisdictions/search/check-duplicates?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          setDupCheck({
            loading: false,
            exact: res.data?.exact || [],
            near: res.data?.near || [],
            otherCounty: res.data?.other_county || [],
            parentScoped: !!res.data?.parent_scoped,
          });
        }
      } catch {
        if (!cancelled) setDupCheck({ loading: false, exact: [], near: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [open, request, p.jurisdiction_name, p.state, p.jurisdiction_type]);

  // Reset comparison + edit state when modal closes / request changes.
  useEffect(() => {
    setComparing(false);
    setActiveMatch(null);
    setIsEditing(false);
    setEditedFields({});
    setHydratedParentName(null);
    setParentCountyOptions([]);
    setApproving(false);
    setApproveResult(null);
    setApproveError('');
  }, [request?.id, open]);

  // Hydrate parent_county_name + load county options for edit dropdown.
  // Runs once per (open, request, state) so we don't refetch on every
  // keystroke. We:
  //   • backfill the display name when the proposal payload only has the
  //     UUID (older requests, or any submitted before the wizard fix);
  //   • populate the list of counties for the proposal's state so edit
  //     mode can render a friendly Select instead of a UUID textbox.
  useEffect(() => {
    if (!open || !request) return;
    const state = p.state;
    if (!state) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        // P0 BUG FIX (Mar 1, 2026 — Parent County dropdown parity).
        // The list endpoint expects `jurisdiction_type` + `page_size`
        // (NOT `type` + `limit`). With the wrong aliases, the request
        // approval/edit modal was returning mixed-type rows capped at
        // 50 — exactly the same bug the wizard had — so real counties
        // could be alphabetically truncated and become unselectable.
        // `active_only=true` matches the wizard's behaviour so retired
        // counties don't leak in.
        // Jun 2026 — switched `jurisdiction_type=county` to the canonical
        // `parent_eligible=true` filter so LA Parishes (and AK/NY
        // Boroughs) appear as parent options when reviewing/editing an
        // LA request. Mirrors the wizard's fetchCounties query.
        const res = await axios.get(
          `${API_BASE_URL}/api/jurisdictions?state=${encodeURIComponent(state)}&parent_eligible=true&active_only=true&page_size=500`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const counties = res.data?.jurisdictions || [];
        setParentCountyOptions(counties);
        // Backfill the display name from the live record if the proposal
        // didn't carry one (older requests, edits in progress).
        if (p.parent_county_id && !p.parent_county_name) {
          const match = counties.find((c) => c.id === p.parent_county_id);
          if (match?.jurisdiction_name) {
            setHydratedParentName(match.jurisdiction_name);
          }
        }
      } catch {
        if (!cancelled) setParentCountyOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, request?.id, p.state, p.parent_county_id, p.parent_county_name]);

  // May 1, 2026 — Force-exit edit mode whenever the underlying request
  // is no longer in `pending` status. Protects against two edge cases:
  //   (a) the user opens an approved/rejected request and somehow
  //       enters edit mode via a stale client cache, and
  //   (b) the status flips server-side (e.g. another reviewer approves
  //       the same request) while this modal is open.
  useEffect(() => {
    if (request && request.status !== 'pending') {
      setIsEditing(false);
      setEditedFields({});
    }
  }, [request?.status]);

  // ─── Edit mode handlers (must be declared BEFORE any early-return so
  // React's hook-order invariant holds across open/closed toggles) ───
  //
  // useCallback so EditCtx consumers don't see a new handler identity on
  // every parent render (keeps context value stable between keystrokes).
  const updateEditedField = useCallback((key, rawValue) => {
    setEditedFields((prev) => {
      const next = { ...prev };
      const original = originalPayload[key];
      const serverEdit = serverEditedPayload[key];
      // If the new value matches the original, drop the key — no diff to send.
      // "Dropping" also re-enables the "Modified" pill logic cleanly.
      if ((rawValue === '' || rawValue === undefined) && original === undefined && serverEdit === undefined) {
        delete next[key];
      } else {
        next[key] = rawValue;
      }
      return next;
    });
  }, [originalPayload, serverEditedPayload]);

  // Batch-update multiple keys atomically. Used by the Parent County
  // dropdown so id + name always change together (otherwise the diff
  // history would show an orphaned "id changed but name stayed" row).
  const updateEditedFields = useCallback((patch) => {
    setEditedFields((prev) => ({ ...prev, ...patch }));
  }, []);

  // NOTE (May 1, 2026): `Field` and `Section` are declared at module
  // scope (above this component) and pull edit state from EditCtx. We
  // build a memoized `editCtx` here and provide it around the modal
  // body. See the "focus-loss" note at the top of this file.
  // May 1, 2026 — `effectiveIsEditing` is the single source of truth
  // that downstream UI should consult. We never let edit mode activate
  // unless the request is still pending. Downgrades gracefully if the
  // status flips mid-session (see the status-watcher useEffect above).
  const isPending = request?.status === 'pending';
  const effectiveIsEditing = isEditing && isPending;
  // PROCESSOR-JURISDICTION-REQUEST-001: processor-lite rows arrive without collector details —
  // approval is blocked until the reviewer completes name / phone / physical address (D4a).
  const isProcessorOrigin = request?.origin === 'search_fulfillment';
  // PJR-001 (Oct 2026): approval gate = payee, phone, physical address (collector name is optional).
  const missingCollector = ['make_checks_payable_to', 'tax_collector_phone', 'tax_collector_physical_address']
    .filter((k) => !String(effectivePayload?.[k] || '').trim());
  // PJR-PARENT-COUNTY-GATE-001: the parent-county gate follows the server-computed `parent_county_required`
  // (models.jurisdiction.requires_parent_county — same rule as Directory + bulk upload). Exempt states/types
  // (RI/MA/NJ/NH/ME/VT/CT …) approve without a county record; `false`/missing flag never blocks.
  const parentRequired = request?.parent_county_required === true;
  const parentMissing = isProcessorOrigin && parentRequired && !effectivePayload?.parent_county_id;
  // PJR-001-B: optional fields must be well-formed (phone/fax 10 digits, email, http(s) URL) before approval.
  const invalidCollector = ['tax_collector_phone', 'tax_collector_fax', 'tax_collector_email', 'tax_collector_website', 'assessor_website']
    .filter((k) => validateCollectorField(k, effectivePayload?.[k]));
  const collectorIncomplete = isPending && (missingCollector.length > 0 || parentMissing || invalidCollector.length > 0);
  // PJR-002-B: when MLC is required, SASE must be an explicit Yes/No before approval (PJR requests only).
  const saseMissing = isPending && isProcessorOrigin && effectivePayload?.mlc_required === true && effectivePayload?.sase_required == null;
  // MLC-VALIDATION-FIX-001 / MLC-FEE-GT-ZERO-001: charges (> $0.00) and method are mandatory too when MLC is required.
  const mlcIncomplete = isPending && isProcessorOrigin && effectivePayload?.mlc_required === true
    && (effectivePayload?.mlc_charges == null || String(effectivePayload.mlc_charges).trim() === '' || !(Number(effectivePayload.mlc_charges) > 0) || !['mail', 'fax', 'email'].includes(String(effectivePayload?.info_obtained_back || '').toLowerCase()));

  // PJR-001 (Oct 2026) — when the submitted parent county is unresolved, tell the reviewer whether the county
  // EXISTS in the directory (→ one-click link through the audited edit path) or is genuinely missing.
  const [parentLookup, setParentLookup] = useState({ loading: false, county: null, checked: false });
  const [linkingParent, setLinkingParent] = useState(false);
  const normCounty = (n) => String(n || '').toUpperCase().replace(/\s+(COUNTY|PARISH)$/, '').replace(/\s+/g, ' ').trim();
  useEffect(() => {
    if (!isReviewer || !isPending || !parentMissing || !effectivePayload?.state) { setParentLookup({ loading: false, county: null, checked: false }); return; }
    let cancelled = false;
    setParentLookup({ loading: true, county: null, checked: false });
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_BASE_URL}/api/jurisdictions?state=${encodeURIComponent(effectivePayload.state)}&parent_eligible=true&active_only=true&page_size=500`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const list = res.data?.jurisdictions || res.data?.items || res.data || [];
        const want = normCounty(effectivePayload?.parent_county_name);
        const county = want ? (list.find((c) => normCounty(c.jurisdiction_name) === want) || null) : null;
        if (!cancelled) setParentLookup({ loading: false, county, checked: true });
      } catch {
        if (!cancelled) setParentLookup({ loading: false, county: null, checked: true });
      }
    })();
    return () => { cancelled = true; };
  }, [isReviewer, isPending, parentMissing, effectivePayload?.state, effectivePayload?.parent_county_name]);

  const handleUseParentCounty = async () => {
    const county = parentLookup.county;
    if (!county) return;
    setLinkingParent(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/api/jurisdiction-requests/${request.id}/edit`,
        { edited_payload: { parent_county_id: county.id, parent_county_name: county.jurisdiction_name } },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Parent county linked: ${displayTitleCase(county.jurisdiction_name)}, ${county.state}`);
      if (onEditApproved) await onEditApproved({ closeModal: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link the parent county');
    } finally {
      setLinkingParent(false);
    }
  };
  const parentExists = parentMissing && parentLookup.checked && !!parentLookup.county;
  const parentNotInDirectory = parentMissing && parentLookup.checked && !parentLookup.county;
  // Collector selected but not yet verified → approval blocked (reviewer must verify before the assignment is created).
  // PJR-APPROVAL-SIMPLIFY-001: no collector-link step in the review UI (approve body carries no collector_id)
  const linkUnverified = false;
  const linkBody = {};

  const editCtx = useMemo(
    () => ({
      isEditing: effectiveIsEditing,
      effectivePayload,
      originalPayload,
      editedFields,
      serverEditedPayload,
      updateEditedField,
      updateEditedFields,
    }),
    [
      effectiveIsEditing,
      effectivePayload,
      originalPayload,
      editedFields,
      serverEditedPayload,
      updateEditedField,
      updateEditedFields,
    ]
  );

  if (!open || !request) return null;

  // A statewide exact-name hit is a CONFIRMED duplicate only when the parent county was established (or the type
  // needs no parent). Other-county "similar" and fuzzy statewide matches are intentionally NOT surfaced to the
  // reviewer (PJR-001, Oct 2026 — user decision); the backend check-duplicates safeguards are unchanged.
  const needsParent = !['county', 'parish'].includes((effectivePayload?.jurisdiction_type || '').toLowerCase());
  const exactConfirmed = dupCheck.parentScoped || !needsParent;
  const hasExact = exactConfirmed && dupCheck.exact.length > 0;
  const countyLabel = (n, st) => n ? `${displayTitleCase(n.replace(/\s+(county|parish)$/i, ''))} County, ${st}` : 'Not set';
  const getTypeLabel = (t) => getJurisdictionTypeLabel(t || 'county');
  const allMatches = dupCheck.exact;
  const compareAgainst = activeMatch || allMatches[0] || null;
  const fieldLabel = (k) => k === 'make_checks_payable_to' ? 'make checks payable to' : k.replace('tax_collector_', '').replace(/_/g, ' ');
  // Single source of truth for "why can't I approve?" — surfaced as the Approve button tooltip.
  const approveBlocker = hasExact ? 'Approval blocked — exact duplicate already in the directory'
    : parentExists ? `Link the parent county first (Use ${displayTitleCase(parentLookup.county.jurisdiction_name)})`
    : parentNotInDirectory ? `Parent county ${displayTitleCase(effectivePayload?.parent_county_name)} is not in the Jurisdiction Directory yet`
    : parentMissing ? 'Parent county must be linked before approval'
    : missingCollector.length > 0 ? `Collector details missing: ${missingCollector.map(fieldLabel).join(', ')} (Edit)`
    : invalidCollector.length > 0 ? `Invalid format: ${invalidCollector.map(fieldLabel).join(', ')} (Edit)`
    : linkUnverified ? 'Verify the selected Tax Collector first'
    : saseMissing ? 'SASE Required must be Yes or No when MLC is required (Edit)'
    : mlcIncomplete ? 'MLC fee must be greater than $0.00 and Info Obtained Back is required when MLC is required (Edit)'
    : '';

  const hasUnsavedEdits = Object.keys(editedFields).length > 0;

  const handleEditToggle = () => {
    // Defense-in-depth: even though the Edit button is gated by
    // request.status === 'pending', guard here too in case the server
    // status changed between list-fetch and click.
    if (!isPending) {
      toast.error(
        `This request has already been ${request.status} and can no longer be edited.`
      );
      return;
    }
    setIsEditing(true);
    // Seed editedFields from server-side edits (if any) so the inputs
    // render with the latest persisted state. A reviewer can then
    // further-edit or revert.
    setEditedFields({});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedFields({});
  };

  const handleSaveChanges = async () => {
    if (!hasUnsavedEdits) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/api/jurisdiction-requests/${request.id}/edit`,
        { edited_payload: editedFields },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Changes saved');
      setIsEditing(false);
      setEditedFields({});
      if (onEditApproved) await onEditApproved({ closeModal: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Single approval path for "Approve Request" and "Save & Approve". The popup stays open until the backend
  // confirms; success flips the body to the confirmation panel, failure surfaces an inline error.
  // A list-refresh failure after a confirmed approval must NOT be shown as an approval failure.
  const runApprove = async (body) => {
    if (approving) return;
    if (hasExact) {
      toast.error('Cannot approve — exact duplicate match in the directory');
      return;
    }
    setApproving(true);
    setApproveError('');
    let data;
    try {
      const token = localStorage.getItem('token');
      ({ data } = await axios.post(
        `${API_BASE_URL}/api/jurisdiction-requests/${request.id}/approve`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      ));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' && detail ? detail : 'Approval failed — the request was not approved. Please try again.';
      setApproveError(msg);
      toast.error(msg);
      setApproving(false);
      return;
    }
    const cl = data?.collector_link;
    setIsEditing(false);
    setEditedFields({});
    if (cl?.assignment_error || cl?.payee_error) toast.warning(cl.assignment_error || cl.payee_error);
    let listRefreshed = false;
    try {
      listRefreshed = onApproved ? !!(await onApproved(data)) : true;
    } catch {
      listRefreshed = false;
    }
    setApproveResult({
      jurisdictionName: data?.jurisdiction?.jurisdiction_name || p.jurisdiction_name,
      jurisdictionType: data?.jurisdiction?.jurisdiction_type || p.jurisdiction_type,
      state: data?.jurisdiction?.state || p.state,
      wasEdited: !!data?.was_edited,
      collectorLink: cl || null,
      listRefreshed,
    });
    setApproving(false);
  };

  const handleSaveAndApprove = () => {
    // Include both local unsaved edits AND already-server-saved edits
    // so the approve endpoint applies the reviewer's complete view.
    runApprove({ edited_payload: { ...serverEditedPayload, ...editedFields }, ...linkBody });
  };

  const retryListRefresh = async () => {
    let ok = false;
    try { ok = onApproved ? !!(await onApproved(null)) : true; } catch { ok = false; }
    setApproveResult((prev) => (prev ? { ...prev, listRefreshed: ok } : prev));
  };

  // Field row helper — see module-scope `Field` component above. The
  // edit state flows in via EditCtx.Provider wrapped around the modal
  // body (below).

  // --- Side-by-side comparison renderer -------------------------------
  // For each comparable field, classify as: identical | differs | only-in-proposed.
  const COMPARE_FIELDS = [
    ['Jurisdiction Name',  'jurisdiction_name'],
    ['Type',               'jurisdiction_type'],
    ['State',              'state'],
    ['Tax Collector Name', 'tax_collector_name'],
    ['Phone',              'tax_collector_phone'],
    ['Email',              'tax_collector_email'],
    ['Fax',                'tax_collector_fax'],
    ['Working Hours',      'tax_collector_working_hours'],
    ['Physical Address',   'tax_collector_physical_address'],
    ['Mailing Address',    'tax_collector_mailing_address'],
    ['Tax Collector Website', 'tax_collector_website'],
    ['Assessor Website',   'assessor_website'],
  ];

  const ComparisonView = () => {
    if (!compareAgainst) return null;
    const norm = (v) => (v === null || v === undefined ? '' : String(v).trim());

    return (
      <div className="space-y-2" data-testid="comparison-view">
        {/* Match selector when more than one match exists */}
        {allMatches.length > 1 && (
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
            <span className="text-xs text-slate-500">Compare against:</span>
            <select
              value={compareAgainst.id}
              onChange={(e) => setActiveMatch(allMatches.find((m) => m.id === e.target.value))}
              className="text-xs border border-slate-200 rounded px-2 py-1"
              data-testid="comparison-match-selector"
            >
              {allMatches.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.jurisdiction_name} ({m.state}) {m.similarity ? `· ${m.similarity}%` : '· exact'}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-3 sticky top-0 bg-white z-10 pb-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2">Existing in Directory</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 px-2">Proposed (this request)</p>
        </div>

        {COMPARE_FIELDS.map(([label, key]) => {
          const lhs = norm(compareAgainst[key]);
          const rhs = norm(p[key]);
          if (!lhs && !rhs) return null;
          const same = lhs === rhs;
          const onlyProposed = !lhs && rhs;
          const cls = same
            ? 'bg-slate-50 border-slate-100'
            : onlyProposed
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-blue-50 border-blue-200';
          return (
            <div
              key={key}
              className={`grid grid-cols-2 gap-x-3 rounded-md border ${cls} text-sm`}
              data-testid={`compare-row-${key}`}
            >
              <div className="px-2 py-1.5">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
                <p className="text-slate-700">{lhs || <span className="text-slate-400 italic">—</span>}</p>
              </div>
              <div className="px-2 py-1.5 border-l border-white/60">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                  {label}
                  {!same && (
                    <span className={`ml-1 ${onlyProposed ? 'text-emerald-600' : 'text-blue-600'}`}>
                      {onlyProposed ? '(new)' : '(differs)'}
                    </span>
                  )}
                </p>
                <p className="text-slate-800 font-medium">{rhs || <span className="text-slate-400 italic">—</span>}</p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <EditCtx.Provider value={editCtx}>
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4 sm:p-6">
      <div
        className="w-full max-w-4xl max-h-[calc(100vh-32px)] sm:max-h-[calc(100vh-48px)] bg-slate-50 rounded-2xl shadow-2xl shadow-indigo-900/30 flex flex-col overflow-hidden jr-pop-in"
        data-testid="request-details-modal"
      >
        {/* Header — PJR-001 premium: blue → purple gradient, skyline motif */}
        <div className="relative shrink-0 px-6 py-5 flex items-center justify-between rounded-t-2xl overflow-hidden" style={{ background: 'linear-gradient(100deg, #1d4ed8 0%, #4f46e5 55%, #6d28d9 100%)' }}>
          <svg aria-hidden className="pointer-events-none absolute right-10 bottom-0 h-[88%] text-white/15" viewBox="0 0 320 90" fill="currentColor"><path d="M0 90V60h18V40h10v20h14V30l8-10 8 10v30h12V48h12v12h10V22h12v38h10V36h14v24h12V44h10v16h14V28h8V18l6-10 6 10v10h8v32h12V50h12v10h10V30h12v30h14V46h12v14h10V34h12v26h8V90z"/></svg>
          <div className="relative flex items-center gap-4 min-w-0">
            <span className="h-14 w-14 rounded-2xl bg-white/15 ring-1 ring-white/25 grid place-items-center shrink-0"><Landmark className="w-7 h-7 text-white" /></span>
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-white font-bold text-[22px] tracking-tight truncate" data-testid="details-title">{displayTitleCase(p.jurisdiction_name) || 'Jurisdiction Request'}</h3>
                <span className="inline-flex items-center rounded-full bg-white/20 ring-1 ring-white/30 px-2.5 py-0.5 text-[12px] font-semibold text-white capitalize" data-testid="details-status-pill">{approveResult ? 'Approved' : request.status === 'pending' ? 'Pending Review' : request.status}</span>
              </div>
              <p className="text-blue-100 text-[14px] mt-1 leading-5 [overflow-wrap:anywhere]">
                Submitted by {request.submitted_by_name || '—'} · {formatET(request.submitted_at)}
                {request.order_number && <> · Order <b className="font-semibold text-white" data-testid="details-order-number">{request.order_number}</b></>}
                {originLabel(request.origin) && <> · from <span data-testid="details-source-label">{originLabel(request.origin)}</span>{request.priority === 'high' ? ' · High priority' : ''}</>}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={approving} className="relative text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/15 transition-colors disabled:opacity-40" data-testid="details-close-btn" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {approveResult ? (
        <>
        {/* PJR-001 (Oct 2026) — backend-confirmed approval. Stays visible until the Admin clicks Done. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-8" data-testid="approval-success-panel">
          <div className="mx-auto max-w-lg text-center jr-pop-in">
            <span className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center ring-8 ring-emerald-50">
              <CheckCircle2 className="w-9 h-9" />
            </span>
            <h4 className="mt-4 text-[20px] font-bold tracking-tight text-slate-900" data-testid="approval-success-title">Jurisdiction Approved Successfully</h4>
            <p className="mt-1 text-sm text-slate-600" data-testid="approval-success-subtitle">
              <b className="text-slate-800">{displayTitleCase(approveResult.jurisdictionName)}</b> has been added to the Jurisdiction Directory{approveResult.wasEdited ? ' with your edits' : ''}.
            </p>
            <dl className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/60 text-left px-4 py-3 grid grid-cols-[130px_1fr] gap-y-2 text-[13px]">
              <dt className="text-slate-500">Jurisdiction</dt>
              <dd className="text-slate-900 font-medium" data-testid="approval-success-jurisdiction">{displayTitleCase(approveResult.jurisdictionName)}{approveResult.jurisdictionType ? ` · ${getTypeLabel(approveResult.jurisdictionType)}` : ''}{approveResult.state ? `, ${approveResult.state}` : ''}</dd>
              <dt className="text-slate-500">Request ID</dt>
              <dd className="text-slate-900" data-testid="approval-success-request-id">
                <span className="font-semibold">JR-{String(request.id).slice(0, 8).toUpperCase()}</span>
                <span className="block font-mono text-[11px] text-slate-500 break-all">{request.id}</span>
              </dd>
              <dt className="text-slate-500">Directory</dt>
              <dd className="text-emerald-700 font-semibold inline-flex items-center gap-1.5" data-testid="approval-success-directory"><CheckCircle2 className="w-3.5 h-3.5" /> Added to the Jurisdiction Directory</dd>
              {approveResult.collectorLink && (
                <>
                  <dt className="text-slate-500">Tax Collector</dt>
                  <dd className="text-slate-900" data-testid="approval-success-collector">Linked to {displayTitleCase(approveResult.collectorLink.collector_name)}{approveResult.collectorLink.payee_applied ? ' · payee updated' : ''}</dd>
                </>
              )}
            </dl>
            {!approveResult.listRefreshed && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-800 flex items-center gap-2 text-left" data-testid="approval-list-refresh-warning">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span className="flex-1">The approval is complete, but the request list could not be refreshed.</span>
                <Button size="sm" variant="outline" onClick={retryListRefresh} className="h-8 rounded-lg bg-white border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="approval-refresh-list-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh list
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 px-5 py-4 border-t border-slate-200 flex items-center justify-end bg-white rounded-b-2xl">
          <Button onClick={onClose} data-testid="approval-success-done-btn"
            className="h-11 rounded-xl px-6 text-[14px] font-semibold transition-all duration-150 hover:-translate-y-px hover:shadow-md active:translate-y-0 text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm shadow-emerald-500/25">
            Done
          </Button>
        </div>
        </>
        ) : (
        <>
        {/* Body — scrollable (header + footer stay fixed) */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        {approveError && (
          <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-50/70 px-4 py-3 flex items-start gap-3 jr-fade-in" data-testid="approval-error-banner" role="alert">
            <span className="h-8 w-8 rounded-xl bg-rose-100 text-rose-600 grid place-items-center shrink-0"><XCircle className="w-4 h-4" /></span>
            <div className="min-w-0 flex-1 text-[12.5px] leading-5 text-slate-700">
              <p className="text-[14px] font-semibold text-rose-900">Approval failed — request not approved</p>
              <p data-testid="approval-error-message">{approveError}</p>
            </div>
            <button type="button" onClick={() => setApproveError('')} className="text-slate-400 hover:text-slate-600 p-1 rounded-md" aria-label="Dismiss" data-testid="approval-error-dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* PJR-001 (Oct 2026) — revision linkage: this request supersedes a rejected one (stored `revision_of_id`).
            The "View original" shortcut was removed by user decision; the reference, reason and audit linkage remain. */}
        {request.revision_of_id && (() => {
          const orig = allRequests.find((r) => r.id === request.revision_of_id);
          const shortId = `JR-${String(request.revision_of_id).slice(0, 8).toUpperCase()}`;
          return (
            <div className="mb-3 rounded-2xl border border-violet-200 bg-violet-50/70 px-4 py-3 flex items-start gap-3" data-testid="revision-linkage-banner">
              <span className="h-8 w-8 rounded-xl bg-violet-100 text-violet-700 grid place-items-center shrink-0"><RotateCcw className="w-4 h-4" /></span>
              <div className="min-w-0 flex-1 text-[12.5px] leading-5 text-slate-700">
                <p className="text-[14px] font-semibold text-slate-900">Resubmission of {shortId}</p>
                <p>
                  Original request rejected{orig?.reviewed_at ? ` ${formatET(orig.reviewed_at)}` : ''}
                  {orig?.rejection_reason && <> — reason: <b className="text-rose-700" data-testid="revision-original-reason">{orig.rejection_reason}</b></>}
                  . Previous submission, reason and audit history are preserved on the original record.
                </p>
              </div>
            </div>
          );
        })()}
        {/* PJR-001 (Oct 2026) — compact verification section: one slate/blue card that lists what blocks
            approval (parent county, collector details, duplicates). Red only for a confirmed same-county
            exact match. Comparison expands on demand. Rules/gates unchanged — presentation only. */}
        {((isReviewer && collectorIncomplete) || (!dupCheck.loading && hasExact)) && (
          <div
            className={`mb-3 rounded-2xl border jr-fade-in ${hasExact ? 'border-rose-200 bg-rose-50/60' : 'border-blue-200/80 bg-gradient-to-br from-blue-50/80 to-slate-50'}`}
            data-testid="verification-section"
          >
            <div className="flex items-start gap-3 px-4 py-3">
              <span className={`h-8 w-8 rounded-xl grid place-items-center shrink-0 ${hasExact ? 'bg-rose-100 text-rose-600' : 'bg-blue-600 text-white'}`}>
                {hasExact ? <AlertCircle className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-[14px] font-semibold leading-5 ${hasExact ? 'text-rose-900' : 'text-slate-900'}`} data-testid="verification-title">
                  {hasExact ? 'Exact duplicate already in directory' : 'Verification required before approval'}
                </p>
                <ul className="mt-1.5 space-y-1 text-[12.5px] leading-5 text-slate-600">
                  {isReviewer && collectorIncomplete && (
                    <li className="flex gap-2" data-testid="collector-required-banner">
                      <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span>
                        {parentMissing && parentExists && <span data-testid="parent-county-exists"><b className="text-slate-800">Parent county {countyLabel(parentLookup.county.jurisdiction_name, p.state)} exists in the directory</b> — link it to this request to continue.</span>}
                        {parentMissing && parentNotInDirectory && <span data-testid="parent-county-not-in-directory"><b className="text-slate-800">Parent county {countyLabel(effectivePayload?.parent_county_name, p.state)} is not in the Jurisdiction Directory yet</b> — a County record must exist before this request can be approved.</span>}
                        {parentMissing && !parentLookup.checked && <><b className="text-slate-800">Parent county verification required</b> — checking the directory for {countyLabel(effectivePayload?.parent_county_name, p.state)}…</>}
                        {!parentMissing && <b className="text-slate-800">Collector details required before approval.</b>}
                        {missingCollector.length > 0 && <> Missing: {missingCollector.map(fieldLabel).join(', ')}.</>}
                        {invalidCollector.length > 0 && <> Invalid format: {invalidCollector.map((k) => k.replace('tax_collector_', '').replace('_', ' ')).join(', ')}.</>}
                      </span>
                    </li>
                  )}
                  {!dupCheck.loading && hasExact && (
                    <li className="flex gap-2" data-testid="duplicate-warning-exact">
                      <span className="mt-[7px] h-1.5 w-1.5 rounded-full shrink-0 bg-rose-500" />
                      <span className="text-rose-700">
                        <b>{dupCheck.exact.length} record{dupCheck.exact.length === 1 ? '' : 's'}</b> with the same name, type and parent county ({countyLabel(dupCheck.exact[0]?.parent_county_name || p.parent_county_name, p.state)}). Approval is blocked.
                      </span>
                    </li>
                  )}
                </ul>
              </div>
              {isReviewer && parentExists && !effectiveIsEditing && (
                <Button size="sm" onClick={handleUseParentCounty} disabled={linkingParent} data-testid="use-parent-county-btn"
                  className="h-9 shrink-0 rounded-xl px-3.5 text-white bg-blue-600 hover:bg-blue-700 transition-all duration-150 hover:-translate-y-px hover:shadow-sm disabled:opacity-60">
                  <Link2 className="w-4 h-4 mr-1.5" /> {linkingParent ? 'Linking…' : `Use ${displayTitleCase(parentLookup.county.jurisdiction_name)}`}
                </Button>
              )}
              {!dupCheck.loading && hasExact && (
                <Button size="sm" variant="outline" onClick={() => setShowCompare((v) => !v)} data-testid="toggle-comparison-btn" aria-expanded={showCompare}
                  className="h-9 shrink-0 rounded-xl px-3.5 bg-white transition-all duration-150 hover:-translate-y-px hover:shadow-sm border-rose-300 text-rose-700 hover:bg-rose-50">
                  <Search className="w-4 h-4 mr-1.5" /> {showCompare ? 'Hide comparison' : 'Compare with existing'}
                </Button>
              )}
            </div>
            {showCompare && hasExact && (
              <div className="border-t px-4 py-3 border-rose-100">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch gap-3 jr-fade-in" data-testid="duplicate-comparison">
                {[
                  { title: 'This Request (Pending)', icon: FileText, tone: 'blue', name: p.jurisdiction_name, type: p.jurisdiction_type, parent: p.parent_county_name, pill: 'bg-blue-50 text-blue-700 border-blue-200' },
                  null,
                  { title: 'Existing Directory Record', icon: Landmark, tone: 'emerald', name: dupCheck.exact[0]?.jurisdiction_name, type: dupCheck.exact[0]?.jurisdiction_type, parent: dupCheck.exact[0]?.parent_county_name, pill: 'bg-rose-50 text-rose-700 border-rose-200' },
                ].map((card, i) => card === null ? (
                  <div key="eq" className="hidden md:grid place-items-center"><span className="h-10 w-10 rounded-full grid place-items-center text-lg font-bold bg-rose-100 text-rose-600">=</span></div>
                ) : (
                  <div key={card.title} className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden" data-testid={i === 0 ? 'compare-request-card' : 'compare-existing-card'}>
                    <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-slate-100">
                      <span className={`h-8 w-8 rounded-lg grid place-items-center ${card.tone === 'blue' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'}`}><card.icon className="w-4 h-4" /></span>
                      <h4 className="text-[14px] font-semibold text-slate-900">{card.title}</h4>
                    </div>
                    <dl className="px-3.5 py-2.5 grid grid-cols-[120px_1fr] gap-y-1.5 text-[13px]">
                      <dt className="text-slate-500">Jurisdiction Name</dt><dd className="text-slate-900 font-medium">{displayTitleCase(card.name) || '—'}</dd>
                      <dt className="text-slate-500">Type</dt><dd className="text-slate-900">{getTypeLabel(card.type)}</dd>
                      <dt className="text-slate-500">State</dt><dd className="text-slate-900">{p.state}</dd>
                      <dt className="text-slate-500">Parent County</dt><dd><span className={`inline-flex rounded-md border px-2 py-0.5 text-[12px] font-semibold ${card.pill}`}>{countyLabel(card.parent, p.state)}</span></dd>
                    </dl>
                  </div>
                ))}
              </div>
              </div>
            )}
            {request.justification && hasExact && (
              <div className="mx-4 mb-3 text-xs bg-white border border-slate-200 rounded-xl p-2.5" data-testid="duplicate-justification-display">
                <span className="font-semibold text-slate-600">Requestor's justification: </span>
                <span className="text-slate-700">{request.justification}</span>
              </div>
            )}
          </div>
        )}

        {/* Status-locked banner — May 1, 2026.
            Once a request is no longer pending, it is read-only. This
            banner makes the state explicit so reviewers don't wonder
            why the Edit/Approve/Reject actions have disappeared. */}
        {!isPending && (
          <div
            className="mb-3 rounded-lg border p-3 bg-slate-50 border-slate-200"
            data-testid="status-locked-banner"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-500" />
              <p className="text-sm text-slate-700">
                This request has already been{' '}
                <span className="font-semibold">
                  {request.status === 'approved' ? 'approved' : request.status === 'rejected' ? 'rejected' : request.status}
                </span>{' '}
                and can no longer be edited.
              </p>
            </div>
          </div>
        )}

          {false ? (
            <ComparisonView />
          ) : (
            <>
              {/* Basic Info */}
              <Section icon={Landmark} title="Basic Information" testId="section-basic" badge={<span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[12px] font-semibold ${p.is_active === false ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}><span className="h-1.5 w-1.5 rounded-full bg-current" />{p.is_active === false ? 'Inactive' : 'Active'}</span>}>
                <Field label="Jurisdiction Name" value={displayTitleCase(p.jurisdiction_name)}
                  editKey="jurisdiction_name" />
                <Field label="Type" value={p.jurisdiction_type ? getTypeLabel(p.jurisdiction_type) : null}
                  editKey="jurisdiction_type" editType="select" editOptions={[
                    { value: '', label: '— Select —' },
                    { value: 'county', label: 'County' },
                    { value: 'city', label: 'City' },
                    { value: 'town', label: 'Town' },
                    { value: 'township', label: 'Township' },
                    { value: 'village', label: 'Village' },
                    { value: 'borough', label: 'Borough' },
                    { value: 'parish', label: 'Parish' },
                    { value: 'municipality', label: 'Municipality' },
                  ]} />
                <Field label="State" value={p.state}
                  editKey="state" editPlaceholder="2-letter code (MA, NJ, ...)" />
                <Field label="FIPS / County Code" value={p.fips} />
                <ParentCountyField
                  payload={p}
                  isEditing={effectiveIsEditing}
                  hydratedName={hydratedParentName}
                  options={parentCountyOptions}
                  originalId={originalPayload.parent_county_id}
                  editedFields={editedFields}
                  serverEditedPayload={serverEditedPayload}
                  updateEditedFields={updateEditedFields}
                />
                <Field label="Active" value={p.is_active} />
              </Section>

              {/* Contact */}
              {(effectiveIsEditing || p.tax_collector_name || p.tax_collector_phone || p.tax_collector_email
                || p.tax_collector_fax || p.tax_collector_working_hours || p.make_checks_payable_to) && (
                <Section icon={Phone} title="Tax Collector Contact" testId="section-contact">
                  <Field label="Name" value={displayTitleCase(p.tax_collector_name)} emptyText="Not provided"
                    editKey="tax_collector_name" />
                  <Field label="Make Checks Payable To" value={p.make_checks_payable_to}
                    editKey="make_checks_payable_to" editPlaceholder="Payee printed on checks (applied to a Tax Collector only on confirmation)" />
                  <Field
                    label="Phone"
                    value={formatUSPhoneDisplay(p.tax_collector_phone)}
                    editKey="tax_collector_phone"
                    editPlaceholder="10 digits" />
                  <Field label="Phone Ext" value={p.tax_collector_phone_ext}
                    editKey="tax_collector_phone_ext" editPlaceholder="e.g. 204" />
                  <Field label="Email" value={p.tax_collector_email} mono
                    editKey="tax_collector_email" editPlaceholder="collector@town.gov" />
                  <Field label="Fax" value={formatUSPhoneDisplay(p.tax_collector_fax)}
                    editKey="tax_collector_fax" editPlaceholder="10 digits" />
                  <Field label="Working Hours" value={p.tax_collector_working_hours} className="sm:col-span-2"
                    editKey="tax_collector_working_hours" editPlaceholder="e.g. Mon-Fri 8am-4:30pm" />
                </Section>
              )}

              {/* PJR-APPROVAL-SIMPLIFY-001: the optional 'Link to existing Tax Collector' panel was removed from the
                  review UI — approval never required it (backend `_validate_collector_link` is a no-op without
                  collector_id); submitted contact/payee are saved on the jurisdiction as before. */}
              {request.collector_link && (
                <Section icon={Phone} title="Collector Linkage (on approval)" testId="section-collector-link">
                  <Field label="Linked Tax Collector" value={displayTitleCase(request.collector_link.collector_name)} />
                  <Field label="Assignment" value={request.collector_link.assignment_id ? 'Created' : (request.collector_link.assignment_error || '—')} />
                  <Field label="Payee Applied" value={request.collector_link.payee_applied ? `Yes — “${request.collector_link.payee_old || 'not set'}” → “${request.collector_link.payee_new}”` : `No — collector payee unchanged${request.collector_link.payee_error ? ` (${request.collector_link.payee_error})` : ''}`} className="sm:col-span-2" />
                </Section>
              )}

              {/* Addresses */}
              {(effectiveIsEditing || p.tax_collector_physical_address || p.tax_collector_mailing_address) && (
                <Section icon={MapPin} title="Address & Website" testId="section-addresses">
                  <Field label="Physical Address" value={displayUSAddress(p.tax_collector_physical_address)} className="sm:col-span-2"
                    editKey="tax_collector_physical_address" editType="textarea"
                    editPlaceholder="Street, City, State ZIP" />
                  <Field label="Mailing Address"  value={displayUSAddress(p.tax_collector_mailing_address)}  className="sm:col-span-2"
                    editKey="tax_collector_mailing_address" editType="textarea"
                    editPlaceholder="PO Box or street address" />
                </Section>
              )}

              {/* Websites */}
              {(effectiveIsEditing || p.tax_collector_website || p.assessor_website) && (
                <Section icon={Globe} title="Websites" testId="section-websites">
                  <Field label="Tax Collector Website" value={p.tax_collector_website} mono className="sm:col-span-2"
                    editKey="tax_collector_website" editPlaceholder="https://..." />
                  <Field label="Assessor Website"      value={p.assessor_website}      mono className="sm:col-span-2"
                    editKey="assessor_website" editPlaceholder="https://..." />
                </Section>
              )}

              {/* PJR-002 / PJR-002-B: MLC & Workflow — every other field depends on "MLC Required?" (inactive when No) */}
              {/* MLC-REVIEW-YESNO-BUG-001: section always renders while editing so MLC Required can never "disappear" mid-edit */}
              {(effectiveIsEditing || p.mlc_required != null || p.sase_required != null || p.info_obtained_back) && (() => {
                const mlcOn = p.mlc_required === true;
                const dis = !mlcOn;
                const naText = 'Not applicable — MLC not required';
                // Toggling MLC Yes → No clears every dependent value in the same edit so stale workflow data is never approved.
                // MLC-REVIEW-YESNO-BUG-001: MLC Required is strictly binary — non-boolean values are ignored (no path to null).
                const onMlcChange = (_k, v) => {
                  if (v === false) updateEditedFields({ mlc_required: false, mlc_charges: null, mlc_24_month_charges: null, mlc_eta_days: null, info_obtained_back: null, sase_required: null });
                  else if (v === true) updateEditedFields({ mlc_required: true });
                };
                return (
                <Section icon={FileText} title="MLC & Workflow" testId="section-mlc"
                  badge={<span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[12px] font-semibold ${mlcOn ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid="mlc-state-badge">{mlcOn ? 'MLC required' : 'MLC not required'}</span>}>
                  <Field label="MLC Required" value={p.mlc_required} emptyText="Not set" editKey="mlc_required" editType="select" onEditChange={onMlcChange}
                    editOptions={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} />
                  {/* MLC-WORKFLOW-CONDITIONAL-UI-001 — dependent fields hidden while MLC Required = No; 'Not set' (null) keeps the existing inactive display */}
                  <MlcCollapse open={p.mlc_required !== false} className="sm:col-span-2 -mt-3" testId="review-mlc-dependent">
                  <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <Field label="SASE Required" value={p.sase_required} emptyText="Not set" editKey="sase_required" editType="select" disabled={dis} disabledText={naText}
                    editOptions={[{ value: '', label: 'Select Yes or No…' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} />
                  <Field label="MLC Charges ($)" value={p.mlc_charges} emptyText="—" editKey="mlc_charges" editPlaceholder="e.g. 50.00" disabled={dis} disabledText={naText} />
                  <Field label="24-Month MLC ($)" value={p.mlc_24_month_charges} emptyText="—" editKey="mlc_24_month_charges" editPlaceholder="e.g. 75.00" disabled={dis} disabledText={naText} />
                  <Field label="MLC ETA (days)" value={p.mlc_eta_days} emptyText="—" editKey="mlc_eta_days" editPlaceholder="e.g. 5" disabled={dis} disabledText={naText} />
                  <Field label="Info Obtained Back" value={p.info_obtained_back ? p.info_obtained_back.charAt(0).toUpperCase() + p.info_obtained_back.slice(1) : null} emptyText="—" editKey="info_obtained_back" editType="select" disabled={dis} disabledText={naText}
                    editOptions={[{ value: '', label: 'Select method…' }, { value: 'mail', label: 'Mail' }, { value: 'fax', label: 'Fax' }, { value: 'email', label: 'Email' }]} />
                  </div>
                  </MlcCollapse>
                </Section>
                );
              })()}

              {/* PJR-002: Supporting documents (secure download) */}
              {request.attachments?.length > 0 && (
                <Section icon={FileText} title={`Supporting Documents (${request.attachments.length})`} testId="section-attachments">
                  <div className="sm:col-span-2 flex flex-wrap gap-2">
                    {request.attachments.map((a) => isAudioAttachment(a) ? (
                      /* PJR-AUDIO-UPLOAD-001: call recording — inline secure player + download (no autoplay) */
                      <div key={a.file_id} className="rounded-xl border border-violet-200 bg-violet-50/40 px-3 py-2 text-[12.5px] w-full space-y-1.5" data-testid={`attachment-${a.file_id}`} data-kind="audio">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileAudio className="h-4 w-4 text-violet-600 shrink-0" />
                          <span className="font-medium text-slate-700 truncate" title={a.filename}>{a.filename}</span>
                          <span className="text-slate-400 shrink-0">· {(a.size / 1024).toFixed(0)} KB · Call recording</span>
                          <button type="button" onClick={() => downloadAttachment(request.id, a)} title="Download recording" className="ml-auto h-7 w-7 rounded-lg grid place-items-center text-slate-500 hover:bg-white hover:text-indigo-600 shrink-0" data-testid={`attachment-download-${a.file_id}`}><Download className="h-4 w-4" /></button>
                        </div>
                        <InlineAudioPlayer src={`${API_BASE_URL}/api/jurisdiction-requests/${request.id}/attachments/${a.file_id}`} authHeaders={{ Authorization: `Bearer ${localStorage.getItem('token')}` }} mime={a.content_type} testId={`attachment-audio-${a.file_id}`} />
                      </div>
                    ) : (
                      /* PJR-REVIEW-FIX-001: explicit View (in-app preview) + Download — the old whole-chip link relied on window.open after an async fetch, which browsers pop-up-block */
                      <div key={a.file_id} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white pl-3 pr-1.5 py-1.5 text-[12.5px] font-medium text-slate-700" data-testid={`attachment-${a.file_id}`} data-kind="document">
                        {a.content_type === 'application/pdf' ? <FileText className="h-4 w-4 text-rose-500 shrink-0" /> : <ImageIcon className="h-4 w-4 text-indigo-500 shrink-0" />}
                        <span className="truncate max-w-[260px]" title={a.filename}>{a.filename}</span>
                        <span className="text-slate-400 shrink-0">· {(a.size / 1024).toFixed(0)} KB</span>
                        <button type="button" onClick={() => setPreviewAttachment(a)} title="View" className="ml-1 h-7 w-7 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition-colors" data-testid={`attachment-view-${a.file_id}`}><Eye className="h-4 w-4" /></button>
                        <button type="button" onClick={() => downloadAttachment(request.id, a)} title="Download" className="h-7 w-7 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition-colors" data-testid={`attachment-download-${a.file_id}`}><Download className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
              {previewAttachment && <AttachmentPreviewDialog requestId={request.id} attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />}

              {/* Notes */}
              {(effectiveIsEditing || p.notes) && (
                <Section icon={FileText} title="Notes" testId="section-notes">
                  {effectiveIsEditing ? (
                    <div className="sm:col-span-2">
                      <Field label="Notes" value={p.notes} editKey="notes" editType="textarea" />
                    </div>
                  ) : (
                    <div className="sm:col-span-2 whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100"
                         data-testid="detail-notes-body">
                      {p.notes}
                    </div>
                  )}
                </Section>
              )}

              {/* Rejection reason — if previously rejected (e.g. opening from Rejected tab) */}
              {request.status === 'rejected' && request.rejection_reason && (
                <Section icon={AlertCircle} title="Rejection Reason" testId="section-rejection">
                  <div className="sm:col-span-2 text-sm text-rose-700 bg-rose-50 rounded-lg p-3 border border-rose-200">
                    {request.rejection_reason}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>

        {/* Footer — Approve / Reject for reviewers when status is pending */}
        <div className="shrink-0 px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-white rounded-b-2xl flex-wrap">
          {effectiveIsEditing ? (
            <>
              {/* Manager edit mode footer — Feb 27, 2026 */}
              {hasUnsavedEdits && (
                <span className="mr-auto text-xs text-slate-500 italic" data-testid="unsaved-edits-indicator">
                  {Object.keys(editedFields).length} unsaved change
                  {Object.keys(editedFields).length === 1 ? '' : 's'}
                </span>
              )}
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                disabled={saving || approving}
                data-testid="details-cancel-edit-btn"
              >
                Cancel Edit
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveChanges}
                disabled={saving || approving || !hasUnsavedEdits}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="details-save-changes-btn"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button
                onClick={handleSaveAndApprove}
                disabled={saving || approving || hasExact || collectorIncomplete || linkUnverified || saseMissing || mlcIncomplete}
                className="h-11 rounded-xl px-5 text-[14px] font-semibold transition-all duration-150 hover:-translate-y-px hover:shadow-md active:translate-y-0 text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm shadow-indigo-500/25 disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none disabled:hover:translate-y-0"
                data-testid="details-save-and-approve-btn"
                title={approveBlocker}
              >
                {approving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Approving…</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> {hasUnsavedEdits ? 'Save & Approve' : 'Approve'}</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={approving} className="h-11 rounded-xl px-5 text-[14px] font-semibold transition-all duration-150 hover:-translate-y-px hover:shadow-md active:translate-y-0 border-slate-200 text-slate-700 hover:bg-slate-50 mr-auto" data-testid="details-dismiss-btn">
                Close
              </Button>
              {isReviewer && request.status === 'pending' && (
                <>
                  {hasExact && (
                    <Button
                      variant="outline"
                      onClick={() => onRejectAsDuplicate && onRejectAsDuplicate(compareAgainst)}
                      disabled={approving}
                      className="h-11 rounded-xl px-5 text-[14px] font-semibold transition-all duration-150 hover:-translate-y-px hover:shadow-md active:translate-y-0 border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100"
                      data-testid="details-reject-as-duplicate-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Reject as Duplicate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={onReject}
                    disabled={approving}
                    className="h-11 rounded-xl px-5 text-[14px] font-semibold transition-all duration-150 hover:-translate-y-px hover:shadow-md active:translate-y-0 border-rose-200 text-rose-700 hover:bg-rose-50"
                    data-testid="details-reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleEditToggle}
                    disabled={approving}
                    className="h-11 rounded-xl px-5 text-[14px] font-semibold transition-all duration-150 hover:-translate-y-px hover:shadow-md active:translate-y-0 border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="details-edit-btn"
                  >
                    <FileText className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button
                    onClick={() => runApprove({ ...linkBody })}
                    disabled={approving || hasExact || collectorIncomplete || linkUnverified || saseMissing || mlcIncomplete}
                    aria-busy={approving}
                    className="h-11 rounded-xl px-5 text-[14px] font-semibold transition-all duration-150 hover:-translate-y-px hover:shadow-md active:translate-y-0 text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm shadow-indigo-500/25 disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none disabled:hover:translate-y-0"
                    title={approveBlocker}
                    data-testid="details-approve-btn"
                  >
                    {approving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Approving…</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve Request</>}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
    </EditCtx.Provider>
  );
};

const JurisdictionRequestsPage = () => {
  const navigate = useNavigate();
  // Memoize user identity so downstream effects don't re-fire on every render.
  // `getUserFromToken()` decodes the JWT and returns a NEW object each call,
  // so deriving booleans from it without memoization churns references and
  // causes effects depending on them to re-run — which previously triggered
  // a fresh fetch on every tab click.
  const currentUser = useMemo(() => getUserFromToken(), []);
  const userRole = currentUser?.role || null;
  const isReviewer = userRole === 'admin' || userRole === 'jurisdiction_manager';
  const isRequestor = userRole === 'jurisdiction_requestor';

  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchParams, setSearchParams] = useSearchParams();
  const category = CATEGORIES.some((c) => c.key === searchParams.get('category')) ? searchParams.get('category') : 'all';
  const setCategory = (key) => { const next = new URLSearchParams(searchParams); if (key === 'all') next.delete('category'); else next.set('category', key); setSearchParams(next, { replace: true }); };
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => { setPage(1); }, [category, statusFilter, search, stateFilter, datePreset, pageSize]);
  const [corrections, setCorrections] = useState([]);
  const [correctionsLoadedFor, setCorrectionsLoadedFor] = useState(null);
  const [issueType, setIssueType] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [resubmitTarget, setResubmitTarget] = useState(null);
  // MLS-SVC-REQ-003 — MLS Service Entity rows open their own review dialog (approve/reject via /api/mls-service-requests)
  const [mlsReview, setMlsReview] = useState(null);
  // Feb 25, 2026 — Details modal target. When set, the read-only
  // RequestDetailsModal opens with all fields from r.payload so the
  // Manager has full context before approving/rejecting.
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [corrReview, setCorrReview] = useState(null);  // JCR-001
  // Feb 25, 2026 — Pre-fill text for the reject prompt (used by the
  // "Reject as Duplicate" shortcut inside the details modal).
  const [rejectInitialReason, setRejectInitialReason] = useState('');

  // Bug fix (Apr 24, 2026): tab clicks previously re-fetched with a `status`
  // query param, flipping `loading=true` every time and causing the whole
  // table area to flash the empty-state skeleton. We now fetch ALL requests
  // once per mount (or on explicit Refresh / mutation) and filter client-
  // side by the active tab — the shell stays mounted, only the row list
  // updates.
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_BASE_URL}/api/jurisdiction-requests`, {
        params: { limit: 500 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = data.items || [];
      setRequests(items);
      return items;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load requests');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Role guard — effect depends only on the stable `userRole` string so it
  // fires exactly once per mount (and again only if the role actually changes).
  useEffect(() => {
    if (!userRole || (!isReviewer && !isRequestor)) {
      navigate('/');
    }
  }, [userRole, isReviewer, isRequestor, navigate]);

  // Initial fetch — runs exactly once on mount. `fetchList` is memoized
  // with `[]` deps so its identity is stable across re-renders.
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Client-side filtered rows for the active tab — derived, not fetched.
  const wantCorrections = category === 'all' || category === 'corrections';
  const unifiedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const since = datePreset === 'all' ? null : Date.now() - Number(datePreset) * 86400000;
    const match = (state, when, hay) => (!stateFilter || (state || '').toUpperCase() === stateFilter)
      && (!since || (when && new Date(when).getTime() >= since))
      && (!q || hay.some((v) => (v || '').toString().toLowerCase().includes(q)));
    const reqRows = category === 'corrections' || statusFilter === 'duplicate' ? [] : requests
      .filter((r) => r.status === statusFilter && (category === 'all' || requestCategory(r) === category)
        && !issueType
        && match((r.payload || {}).state || r.state, r.submitted_at, [r.payload?.jurisdiction_name, r.payload?.entity_name, r.payload?.name, r.jurisdiction_name, r.proposed?.entity_name, r.id, r.submitted_by_name, r.payload?.notes, r.payload?.justification, r.order_number, r.submission_note]))
      .map((r) => ({ kind: 'request', id: r.id, at: r.submitted_at, r }));
    const corrRows = !wantCorrections ? [] : corrections
      .filter((c) => c.status === statusFilter && (!issueType || c.request_type === issueType)
        && match(c.jurisdiction?.state, c.submitted_at, [c.jurisdiction?.jurisdiction_name, c.jurisdiction_id, c.id, c.submitted_by_name, c.description, c.suggested_collector_name]))
      .map((c) => ({ kind: 'correction', id: c.id, at: c.submitted_at, c }));
    return [...reqRows, ...corrRows].sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  }, [requests, corrections, statusFilter, category, stateFilter, datePreset, search, issueType, wantCorrections]);
  const visibleRequests = unifiedRows;
  const pagedRows = useMemo(() => unifiedRows.slice((page - 1) * pageSize, page * pageSize), [unifiedRows, page, pageSize]);
  const stateOptions = useMemo(() => [...new Set(requests.map((r) => ((r.payload || {}).state || r.state || '').toUpperCase()).filter(Boolean))].sort(), [requests]);
  const categoryCounts = useMemo(() => {
    const acc = requests.filter((r) => r.status === 'pending').reduce((a, r) => { const k = requestCategory(r); a[k] = (a[k] || 0) + 1; a.all += 1; return a; }, { all: 0 });
    if (correctionsLoadedFor === 'pending') { acc.corrections = corrections.filter((c) => c.status === 'pending').length; acc.all += acc.corrections; }
    return acc;
  }, [requests, corrections, correctionsLoadedFor]);

  const approveMls = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_BASE_URL}/api/mls-service-requests/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data?.link_result === 'linked' ? 'Entity approved, added to the directory and linked to the originating order entry' : `Entity approved and added to the directory — ${data?.request?.link_result_label || 'not linked to the order'}`);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    }
  };

  const approve = async (id, extra = {}) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_BASE_URL}/api/jurisdiction-requests/${id}/approve`,
        extra,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const cl = data?.collector_link;
      toast.success(cl ? `Request approved — jurisdiction created, linked to ${cl.collector_name}${cl.payee_applied ? ', payee updated' : ''}` : 'Request approved — jurisdiction created');
      if (cl?.assignment_error || cl?.payee_error) toast.warning(cl.assignment_error || cl.payee_error);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    }
  };

  const reject = async (id, reason) => {
    try {
      const token = localStorage.getItem('token');
      const isResearch = requests.find((r) => r.id === id)?.request_type === 'research_links';
      const url = isResearch
        ? `${API_BASE_URL}/api/research-link-requests/${id}/reject`
        : `${API_BASE_URL}/api/jurisdiction-requests/${id}/reject`;
      await axios.post(
        url,
        isResearch ? { reason } : { rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Request rejected');
      setRejectingId(null);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    }
  };

  // RESEARCH-LINKS-001 — mark a research-links request fulfilled (requires
  // at least one active link configured on the jurisdiction, enforced
  // server-side).
  const completeResearch = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/research-link-requests/${id}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Research links request completed — requester notified');
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Completion failed');
    }
  };

  const fetchCorrections = useCallback(async (status) => {
    const list = isReviewer ? listCorrectionRequests : listMyCorrectionRequests;
    const items = []; let pg = 1; let totalPages = 1;
    do {
      const data = await list({ status, page: pg, page_size: 100 });
      items.push(...(data.items || []));
      totalPages = data.pagination?.total_pages || 1; pg += 1;
    } while (pg <= totalPages);
    return items;
  }, [isReviewer]);
  // Fetch the corrections list once per status view (only when the view needs it); refetched after an action/Refresh.
  useEffect(() => {
    if (!wantCorrections || correctionsLoadedFor === statusFilter) return;
    let alive = true;
    fetchCorrections(statusFilter).then((items) => { if (alive) { setCorrections(items); setCorrectionsLoadedFor(statusFilter); } })
      .catch(() => { if (alive) toast.error('Failed to load collector corrections'); });
    return () => { alive = false; };
  }, [wantCorrections, statusFilter, correctionsLoadedFor, fetchCorrections]);
  const refreshAll = useCallback(() => { setCorrectionsLoadedFor(null); fetchList(); }, [fetchList]);
  const inCategory = useMemo(() => requests.filter((r) => category === 'all' || requestCategory(r) === category), [requests, category]);
  const rowCounts = useMemo(() => {
    const base = category === 'corrections' ? { pending: 0, approved: 0, rejected: 0 } : {
      pending: inCategory.filter((r) => r.status === 'pending').length,
      approved: inCategory.filter((r) => r.status === 'approved').length,
      rejected: inCategory.filter((r) => r.status === 'rejected').length,
    };
    if (wantCorrections && correctionsLoadedFor === statusFilter) base[statusFilter] = (base[statusFilter] || 0) + corrections.filter((c) => c.status === statusFilter).length;
    return base;
  }, [inCategory, category, wantCorrections, correctionsLoadedFor, statusFilter, corrections]);
  return (
    <div className="jx-requests min-h-screen bg-slate-50 px-6 py-5" data-testid="jurisdiction-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3.5 min-w-0">
          <span className="jx-head-icon" aria-hidden="true"><LayoutList /></span>
          <div className="min-w-0">
            <h1 className="jx-title">
              {isReviewer ? 'Jurisdiction Requests' : 'My Jurisdiction Requests'}
            </h1>
            <p className="jx-subtitle">
              {isReviewer
                ? 'Review, approve and manage jurisdiction requests, research links, MLS service entities and collector corrections.'
                : 'Track the status of your submitted proposals and collector corrections.'}
            </p>
          </div>
        </div>
        <Button onClick={refreshAll} variant="outline" disabled={loading} className="h-9 rounded-[10px] text-[13px] font-semibold border-[#dfe5ef] shrink-0" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Category control */}
      <div className="flex items-center gap-2 mb-3.5 overflow-x-auto pb-0.5" role="tablist" data-testid="jr-category-tabs">
        {CATEGORIES.map((c) => {
          const active = category === c.key; const Icon = c.icon;
          const count = categoryCounts[c.key] || 0;
          return (
            <button key={c.key} type="button" role="tab" aria-selected={active} onClick={() => setCategory(c.key)} data-testid={`jr-category-${c.key}`}
              className="jx-cat-tab">
              <Icon className="h-4 w-4" />{c.label}
              {count !== null && count > 0 && <span className="jx-cat-count">{count}</span>}
            </button>
          );
        })}
      </div>


      {/* Status tabs */}
      <div className="jx-status-tabs">
        {['pending', 'approved', 'rejected', ...(wantCorrections ? ['duplicate'] : [])].map((s) => {
          const M = STATUS_META[s] || STATUS_META.duplicate;
          const Icon = M.icon;
          const active = statusFilter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className="jx-status-tab"
              data-active={active ? 'true' : 'false'}
              data-testid={`tab-${s}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {M.label}
              {rowCounts[s] > 0 && (
                <span className={`jx-status-count ml-0.5 px-1.5 py-0.5 rounded-full font-semibold ${M.className}`}>
                  {rowCounts[s]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="jr-toolbar">
        <div className="relative flex-1 min-w-[260px]">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search jurisdiction, request ID, submitter, or description..." className="jx-control w-full pl-9 pr-3" data-testid="jr-search" />
        </div>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="jx-control px-3" data-testid="jr-state-filter">
          <option value="">All States</option>{stateOptions.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
        {wantCorrections && (<>
          <select value={issueType} onChange={(e) => setIssueType(e.target.value)} className="jx-control px-3" data-testid="jr-issue-filter">
            <option value="">All Issue Types</option>{Object.entries(ISSUE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </>)}
        <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} className="jx-control px-3" data-testid="jr-date-filter">
          {DATE_PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {(search || stateFilter || datePreset !== 'all' || issueType) && <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStateFilter(''); setDatePreset('all'); setIssueType(''); }} data-testid="jr-clear-filters">Clear</Button>}
      </div>

      {/* Table */}
      <div className="jx-table-card rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-hidden" data-testid="jr-table-wrap">
        <table className="w-full table-fixed text-[13px] jr-table" data-testid="jr-table">
          <colgroup>
            <col />
            <col className="w-[118px]" />
            <col className="w-[13%]" />
            <col className="w-[60px]" />
            <col className="w-[12%]" />
            <col className="w-[108px]" />
            <col className="w-[112px]" />
            <col className="w-[172px] min-[1700px]:w-[330px]" />
          </colgroup>
          <thead className="tf-thead uppercase text-[11px] tracking-wider">
            <tr className="align-middle">
              <th className="px-2 py-2.5 text-left leading-tight">Jurisdiction</th>
              <th className="px-2 py-2.5 text-left leading-tight">Category</th>
              <th className="px-2 py-2.5 text-left leading-tight">Details / Issue Type</th>
              <th className="px-2 py-2.5 text-left leading-tight">State</th>
              <th className="px-2 py-2.5 text-left leading-tight">Submitted By</th>
              <th className="px-2 py-2.5 text-left leading-tight">Submitted</th>
              <th className="px-2 py-2.5 text-left leading-tight">Status</th>
              <th className="px-2 py-2.5 text-right leading-tight">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibleRequests.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-4 py-16 text-center text-slate-400" data-testid="empty-state">
                  No {statusFilter} requests.
                </td>
              </tr>
            )}
            {pagedRows.map((row) => {
              if (row.kind === 'correction') {
                const c = row.c; const CM = STATUS_META[c.status] || STATUS_META.pending; const CIcon = CM.icon;
                return (
                  <tr key={`c-${c.id}`} className="hover:bg-slate-50 transition-colors" data-testid={`correction-row-${c.id}`} data-request-type="collector_correction">
                    <td className={`${TD} font-medium text-slate-800 [overflow-wrap:anywhere]`}>{c.jurisdiction?.jurisdiction_name || c.jurisdiction_id}<div className="text-[11px] text-slate-500 font-normal">{c.jurisdiction?.state || '—'} · {c.tax_period}{c.tax_category_code ? ` · ${c.tax_category_code}` : ''}</div></td>
                    <td className={TD}><span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 leading-tight">Collector Correction</span></td>
                    <td className={`${TD} text-slate-700`}><div className="truncate" title={c.description || ''}>{ISSUE_TYPE_LABELS[c.request_type] || c.request_type}</div>{c.affected_orders_count > 0 && <div className="text-[11px] text-slate-400 truncate">{c.affected_orders_count} affected order{c.affected_orders_count === 1 ? '' : 's'}</div>}</td>
                    <td className={`${TD} text-slate-700`}>{c.jurisdiction?.state || '—'}</td>
                    <td className={`${TD} text-xs text-slate-700`}><ByCell name={c.submitted_by_name} /></td>
                    <td className={TD}><SubmittedCell iso={c.submitted_at} /></td>
                    <td className={TD}><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${c.status === 'duplicate' ? 'bg-slate-100 text-slate-600 border-slate-200' : CM.className}`}>{c.status !== 'duplicate' && CIcon && <CIcon className="h-3 w-3" />}{c.status === 'duplicate' ? 'Duplicate' : CM.label}</span></td>
                    <td className={`${TD} text-right`}><CorrectionReviewActions row={c} isReviewer={isReviewer} formatDate={formatET} onChanged={() => setCorrectionsLoadedFor(null)} /></td>
                  </tr>
                );
              }
              const r = row.r;
              const M = STATUS_META[r.status] || STATUS_META.pending;
              const MIcon = M.icon;
              const p = r.payload || {};
              const isResearch = r.request_type === 'research_links';
              const isMls = r.request_type === 'mls_service_entity';
              const isCorr = r.request_type === 'jurisdiction_correction';  // JCR-001 — reviewed only in CorrectionReviewModal
              return (
                <tr
                  key={r.id}
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                  data-testid={`request-row-${r.id}`}
                  data-request-type={r.request_type || 'new_jurisdiction'}
                  onClick={() => { if (isCorr) setCorrReview(r); else if (isMls) setMlsReview({ req: r, reject: false }); else if (!isResearch) setDetailsTarget(r); }}
                >
                  <td className={`${TD} font-medium text-slate-800 [overflow-wrap:anywhere]`}>
                    {isMls ? (r.proposed?.entity_name || '—') : isResearch ? r.jurisdiction_name : (displayTitleCase(p.jurisdiction_name) || '—')}
                    {!isMls && !isResearch && <div className="text-[11px] text-slate-500 font-normal mt-0.5" data-testid={`request-state-name-${r.id}`}>{STATE_NAMES[p.state] || p.state || '—'}</div>}
                    {isMls && (
                      <div className="text-[11px] text-slate-500 font-normal mt-0.5" data-testid={`mls-row-sub-${r.id}`}>
                        {SERVICE_LABELS[r.service_type] || r.service_type} · {r.jurisdiction_name} <span className="capitalize">({r.jurisdiction_level})</span>{r.order_number ? <> · Order <b className="font-semibold text-slate-700">{r.order_number}</b></> : null}{r.origin ? ` · ${originText(r.origin)}` : ''}
                      </div>
                    )}
                    {isResearch && r.order_number && (
                      <div className="text-[11px] text-slate-400 font-normal mt-0.5">Order {r.order_number}</div>
                    )}
                  </td>
                  <td className={`${TD} text-slate-600`}>
                    {isMls ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-violet-50 text-violet-700 border-violet-200 font-semibold leading-tight" data-testid={`mls-type-badge-${r.id}`}>
                        MLS Service Entity
                      </span>
                    ) : isCorr ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-amber-50 text-amber-700 border-amber-200 font-semibold leading-tight" data-testid={`correction-type-badge-${r.id}`}>
                        Directory Correction
                      </span>
                    ) : isResearch ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-blue-50 text-blue-700 border-blue-200 font-semibold leading-tight" data-testid={`research-type-badge-${r.id}`}>
                        Research Links
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-slate-50 text-slate-700 border-slate-200 font-semibold leading-tight" data-testid={`jurisdiction-type-badge-${r.id}`}>
                        New Jurisdiction
                      </span>
                    )}
                  </td>
                  <td className={`${TD} text-slate-700`}><div className="truncate" title={(isMls ? (r.proposed?.service_type || r.proposed?.notes) : isResearch ? `${(r.proposed_links || r.links || []).length || ''} research link(s)` : (p.justification || p.notes)) || ''}>{isMls ? 'Add MLS service entity' : isResearch ? 'Add research links' : isCorr ? <span data-testid={`jcr-issue-type-${r.id}`}>{CORRECTION_TYPE_LABEL(r.changes)}</span> : (r.status === 'approved' && r.created_jurisdiction_id ? 'Jurisdiction created' : `Create ${p.jurisdiction_type ? getJurisdictionTypeLabel(p.jurisdiction_type) : 'jurisdiction'}`)}</div></td>
                  <td className={`${TD} text-slate-600`}>{(isResearch || isMls) ? (r.state || '—') : (p.state || '—')}</td>
                  <td className={`${TD} text-xs text-slate-600`}><ByCell name={r.submitted_by_name} /></td>
                  <td className={TD}><SubmittedCell iso={r.submitted_at} /></td>
                  <td className={TD}>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border whitespace-nowrap ${M.className}`}>
                      <MIcon className="w-3 h-3" /> {M.label}
                    </span>
                    {r.status === 'rejected' && r.rejection_reason && !isCorr && (
                      <div className="text-[11px] text-rose-600 mt-1 line-clamp-2" title={r.rejection_reason}>
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        {r.rejection_reason}
                      </div>
                    )}
                    {r.status === 'approved' && isMls && (
                      <div className={`text-[11px] leading-tight mt-1 ${r.link_result === 'linked' ? 'text-emerald-600' : 'text-blue-600'}`} data-testid={`mls-link-result-${r.id}`}>
                        {r.link_result === 'linked' ? 'Linked to order' : 'In directory · not linked'}
                      </div>
                    )}
                    {r.status === 'approved' && isResearch && (
                      <div className="text-[11px] leading-tight text-slate-400 mt-1">Links configured</div>
                    )}
                    {r.status === 'approved' && !isMls && !isResearch && r.approved_jurisdiction_id && (
                      <div className="text-[11px] leading-tight text-slate-400 mt-1">Created in directory</div>
                    )}
                  </td>
                  <td className={`${TD} text-right`} onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1 items-center justify-end">
                      {isCorr ? (
                        <Button size="sm" variant="outline" onClick={() => setCorrReview(r)} data-testid={`view-details-${r.id}`} className="h-8 px-2.5 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50" title="Review correction">
                          <Pencil className="w-3.5 h-3.5" /><span className="ml-1">Review</span>
                        </Button>
                      ) : isMls ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setMlsReview({ req: r, reject: false })} data-testid={`view-details-${r.id}`} className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900" title="View details">
                            <Eye className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">View</span>
                          </Button>
                          {isReviewer && r.status === 'pending' && (
                            <>
                              <Button size="sm" onClick={() => approveMls(r.id)} className="h-8 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" title="Approve" data-testid={`approve-${r.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Approve</span>
                              </Button>
                              <Button size="sm" className="h-8 px-2.5 text-xs" variant="outline" onClick={() => setMlsReview({ req: r, reject: true })} title="Reject" data-testid={`reject-${r.id}`}>
                                <XCircle className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Reject</span>
                              </Button>
                            </>
                          )}
                        </>
                      ) : isResearch ? (
                        <>
                          {isReviewer && r.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2.5 text-xs"
                                onClick={() => navigate(`/admin/jurisdictions?focus=${r.jurisdiction_id}&tab=research`)}
                                title="Configure links"
                                data-testid={`configure-links-${r.id}`}
                              >
                                <Globe className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Configure</span>
                              </Button>
                              <Button size="sm" onClick={() => completeResearch(r.id)} className="h-8 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" title="Complete" data-testid={`complete-research-${r.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Complete</span>
                              </Button>
                              <Button size="sm" className="h-8 px-2.5 text-xs" variant="outline" onClick={() => setRejectingId(r.id)} title="Reject" data-testid={`reject-${r.id}`}>
                                <XCircle className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Reject</span>
                              </Button>
                            </>
                          )}
                        </>
                      ) : (
                      <>
                      {/* View Details — visible to ALL roles, every status. */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDetailsTarget(r)}
                        data-testid={`view-details-${r.id}`}
                        className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900"
                        title="View details"
                      >
                        <Eye className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">View</span>
                      </Button>
                      {isReviewer && r.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => approve(r.id)} className="h-8 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" title="Approve" data-testid={`approve-${r.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Approve</span>
                          </Button>
                          <Button size="sm" className="h-8 px-2.5 text-xs" variant="outline" onClick={() => setRejectingId(r.id)} title="Reject" data-testid={`reject-${r.id}`}>
                            <XCircle className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Reject</span>
                          </Button>
                        </>
                      )}
                      {isRequestor && r.status === 'rejected' && (
                        <Button size="sm" className="h-8 px-2.5 text-xs" variant="outline" onClick={() => setResubmitTarget(r)} title="Revise & Resubmit" data-testid={`resubmit-${r.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" /><span className="hidden min-[1700px]:inline ml-1">Revise & Resubmit</span>
                        </Button>
                      )}
                      </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {visibleRequests.length > 0 && (
          <Pagination total={visibleRequests.length} page={page} pageSize={pageSize} sizes={[10, 25, 50]} onPage={setPage} onPageSize={setPageSize} noun="requests" testid="jr-pagination" />
        )}
      </div>

      <RejectPrompt
        open={!!rejectingId}
        onCancel={() => { setRejectingId(null); setRejectInitialReason(''); }}
        onConfirm={(reason) => { reject(rejectingId, reason); setRejectInitialReason(''); }}
        initialReason={rejectInitialReason}
      />

      {/* Feb 25, 2026 — Read-only details modal. Opens via row click or
          the "View" action; gives Managers full payload context before
          they hit Approve / Reject. Approve/Reject inside the modal
          dispatch to the same handlers and close the modal on success. */}
      {mlsReview && (
        <MlsEntityReviewDialog req={mlsReview.req} initialReject={mlsReview.reject} onClose={() => setMlsReview(null)} onDone={fetchList} />
      )}
      {corrReview && <CorrectionReviewModal request={corrReview} isReviewer={isReviewer} onClose={() => setCorrReview(null)} onChanged={() => fetchList()} />}
      <RequestDetailsModal
        open={!!detailsTarget}
        request={detailsTarget}
        isReviewer={isReviewer}
        allRequests={requests}
        onClose={() => setDetailsTarget(null)}
        onApproved={async () => {
          // PJR-001 (Oct 2026) — approval is confirmed inside the modal; this only refreshes the list.
          // Returns false (never throws) when the refresh fails so the modal keeps its success state.
          const fresh = await fetchList();
          return fresh !== null;
        }}
        onReject={() => {
          const id = detailsTarget?.id;
          setDetailsTarget(null);
          if (id) setRejectingId(id);
        }}
        onRejectAsDuplicate={(matchedJurisdiction) => {
          const id = detailsTarget?.id;
          // Pre-fill rejection reason with the matched jurisdiction's
          // identity so the requestor sees exactly which record we're
          // pointing them to.
          const matchLabel = matchedJurisdiction
            ? `${matchedJurisdiction.jurisdiction_name} (${matchedJurisdiction.state})`
            : 'an existing jurisdiction';
          const matchId = matchedJurisdiction?.id ? ` — id ${matchedJurisdiction.id}` : '';
          setRejectInitialReason(
            `Duplicate of ${matchLabel}${matchId}. Please use the existing directory entry instead.`
          );
          setDetailsTarget(null);
          if (id) setRejectingId(id);
        }}
        onEditApproved={async ({ closeModal }) => {
          // Feb 27, 2026 — called after PATCH /edit (closeModal=false) or
          // POST /approve-with-edits (closeModal=true). Re-fetch the list
          // and, if the modal is staying open, refresh the modal's request
          // doc so `payload_edited` / `edit_diff` render.
          if (closeModal) {
            setDetailsTarget(null);
          }
          const fresh = await fetchList();
          if (!closeModal && detailsTarget?.id) {
            // Use the freshly fetched list — `requests` here is a stale closure and would re-select the old doc.
            const refreshed = (fresh || requests || []).find((r) => r.id === detailsTarget.id);
            if (refreshed) setDetailsTarget(refreshed);
          }
        }}
      />

      {/* Revise & Resubmit wizard — reuses the shared Wizard in a dedicated
          mode. On save it hits POST /{id}/resubmit via the wizard's API then
          we re-fetch. */}
      {resubmitTarget && (
        <JurisdictionWizardModal
          isOpen
          mode="request"
          jurisdiction={{ ...resubmitTarget.payload, _resubmitOf: resubmitTarget.id }}
          onClose={() => setResubmitTarget(null)}
          onSave={async (_newPayload) => {
            // The wizard in mode='request' POSTs to /jurisdiction-requests
            // (a fresh submission). For a TRUE resubmit we call the dedicated
            // endpoint so `revision_of_id` is recorded.
            try {
              const token = localStorage.getItem('token');
              await axios.post(
                `${API_BASE_URL}/api/jurisdiction-requests/${resubmitTarget.id}/resubmit`,
                { payload: _newPayload?.request?.payload || _newPayload },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success('Revised request submitted');
            } catch (e) {
              // If the wizard already submitted once as a new request, we
              // silently rely on that — worst case the user has both a
              // rejected + a fresh pending row, which is acceptable.
            }
            setResubmitTarget(null);
            await fetchList();
          }}
        />
      )}
    </div>
  );
};

export default JurisdictionRequestsPage;
